import { History, Pencil } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import toast from "react-hot-toast";
import { keys } from "../../../api/queryKeys.ts";
import { api, type SystemPrompt } from "../../../api/client.ts";
import IdentityCell from "../../../components/IdentityCell.tsx";
import RowActions from "../../../components/RowActions.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import PageHeader from "../../../components/PageHeader.tsx";

function SystemPromptsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SystemPrompt | null>(null);
  const [editText, setEditText] = useState("");

  const { data: prompts, isLoading } = useQuery({
    queryKey: keys.systemPrompt.list(),
    queryFn: api.getSystemPrompts,
  });

  const updateMutation = useMutation({
    mutationFn: ({ slug, text }: { slug: string; text: string }) =>
      api.updateSystemPrompt(slug, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.systemPrompt.list() });
      toast.success("Prompt saved");
      setEditing(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: (slug: string) => api.resetSystemPrompt(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.systemPrompt.list() });
      toast.success("Prompt reset to default");
    },
  });

  const handleEdit = (prompt: SystemPrompt) => {
    setEditing(prompt);
    setEditText(prompt.text);
  };

  const handleSave = () => {
    if (editing) {
      updateMutation.mutate({ slug: editing.slug, text: editText });
    }
  };

  return (
    <>
      <PageHeader
        title="System prompts"
        description={
          <>
            Manage the system prompts used by LLM agents. Customized prompts override the built-in
            defaults and take effect immediately.
          </>
        }
      />
      <TableCard loading={isLoading}>
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(prompts ?? []).map((p) => (
              <TableRow
                key={p.slug}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => handleEdit(p)}
              >
                <TableCell>
                  <IdentityCell
                    name={p.name}
                    caption={<Box component="span" sx={{ fontFamily: "monospace" }}>{p.slug}</Box>}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {p.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={p.is_override ? "Customized" : "Default"}
                    color={p.is_override ? "warning" : "default"}
                  />
                </TableCell>
                <TableCell padding="checkbox" align="right">
                  <RowActions
                    label={p.name}
                    actions={[
                      { label: "Edit", icon: <Pencil fontSize="small" />, onClick: () => handleEdit(p) },
                      {
                        label: "Reset to default",
                        icon: <History fontSize="small" />,
                        hidden: !p.is_override,
                        onClick: () => resetMutation.mutateAsync(p.slug),
                        confirm: {
                          title: `Reset to default: ${p.name}`,
                          body: "Your customizations to this prompt will be lost.",
                          confirmLabel: "Reset",
                          color: "warning",
                        },
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            ))}
            {prompts?.length === 0 && <EmptyTableRow colSpan={4}>No system prompts</EmptyTableRow>}
          </TableBody>
        </ScrollTable>
      </TableCard>
      {/* Edit Dialog */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit: {editing?.name}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            {editing?.description}
          </Typography>
          <TextField
            multiline
            fullWidth
            minRows={10}
            maxRows={25}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: "monospace",
                fontSize: "0.875rem",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            loading={updateMutation.isPending}
            disabled={editText === editing?.text}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SystemPromptsPage;
